<template>
  <article class="invoice-create-card invoice-create-card--accent-info invoice-detail-fiscal-card">
    <header class="invoice-create-card__head">
      <h3>
        <i class="fa fa-calculator invoice-create-section-icon invoice-create-section-icon--info" aria-hidden="true"></i>
        {{ text.detail.cards.fiscalSummary.title }}
      </h3>

      <button
        type="button"
        class="invoice-detail-fiscal-toggle"
        @click="showFullSummary = !showFullSummary"
      >
        <i class="fa" :class="showFullSummary ? 'fa-angle-up' : 'fa-angle-down'" aria-hidden="true"></i>
        {{ showFullSummary ? fiscalToggleLessLabel : fiscalToggleMoreLabel }}
      </button>
    </header>

    <dl class="invoice-detail-fiscal-list">
      <div class="invoice-detail-fiscal-row">
        <dt>{{ text.detail.cards.fiscalSummary.fields.receiverRfc }}</dt>
        <dd>{{ receiverRfc }}</dd>
      </div>

      <div class="invoice-detail-fiscal-row invoice-detail-fiscal-row--with-separator">
        <dt>{{ text.detail.cards.fiscalSummary.fields.businessName }}</dt>
        <dd>{{ receiverBusinessName }}</dd>
      </div>

      <div class="invoice-detail-fiscal-row">
        <dt>{{ billingDataText.modal.form.fiscalConfiguration.fields.cfdiUse }}</dt>
        <dd>{{ receiverCfdiUse }}</dd>
      </div>

      <div class="invoice-detail-fiscal-row">
        <dt>{{ text.detail.cards.fiscalSummary.fields.paymentMethod }}</dt>
        <dd>{{ resolvedPaymentMethod }}</dd>
      </div>

      <div class="invoice-detail-fiscal-row invoice-detail-fiscal-row--with-separator">
        <dt>{{ text.detail.cards.fiscalSummary.fields.paymentForm }}</dt>
        <dd>{{ resolvedPaymentForm }}</dd>
      </div>

      <div class="invoice-detail-fiscal-row">
        <dt>{{ resolvedIssueDateLabel }}</dt>
        <dd>{{ resolvedDateTime }}</dd>
      </div>

      <div class="invoice-detail-fiscal-row">
        <dt>{{ text.detail.cards.fiscalSummary.fields.folio }}</dt>
        <dd>{{ resolvedSerialFolio }}</dd>
      </div>

      <div class="invoice-detail-fiscal-row">
        <dt>{{ text.detail.cards.fiscalSummary.fields.uuid }}</dt>
        <dd>{{ resolvedUuid }}</dd>
      </div>

      <div v-if="showForeignCurrencyRow" class="invoice-detail-fiscal-row">
        <dt>{{ currencyExchangeLabel }}</dt>
        <dd>{{ currencyExchangeValue }}</dd>
      </div>
    </dl>

    <div v-if="showFullSummary" class="invoice-detail-fiscal-expanded">
      <section class="invoice-detail-fiscal-block">
        <h4>{{ text.detail.cards.fiscalSummary.sections.emitter }}</h4>
        <dl class="invoice-detail-fiscal-list">
          <div class="invoice-detail-fiscal-row">
            <dt>{{ text.detail.cards.fiscalSummary.fields.rfc }}</dt>
            <dd>{{ issuerRfc }}</dd>
          </div>

          <div class="invoice-detail-fiscal-row">
            <dt>{{ text.detail.cards.fiscalSummary.fields.businessName }}</dt>
            <dd>{{ issuerBusinessName }}</dd>
          </div>

          <div class="invoice-detail-fiscal-row">
            <dt>{{ billingDataText.modal.form.fiscalConfiguration.fields.fiscalRegime }}</dt>
            <dd>{{ issuerTaxRegime }}</dd>
          </div>

          <div class="invoice-detail-fiscal-row">
            <dt>{{ text.detail.cards.fiscalSummary.fields.fiscalResidence }}</dt>
            <dd>{{ issuerFiscalResidence }}</dd>
          </div>
        </dl>
      </section>

      <section class="invoice-detail-fiscal-block">
        <h4>{{ text.detail.cards.fiscalSummary.sections.receiver }}</h4>
        <dl class="invoice-detail-fiscal-list">
          <div class="invoice-detail-fiscal-row">
            <dt>{{ billingDataText.modal.form.fiscalConfiguration.fields.fiscalRegime }}</dt>
            <dd>{{ receiverTaxRegime }}</dd>
          </div>

          <div class="invoice-detail-fiscal-row">
            <dt>{{ text.detail.cards.fiscalSummary.fields.fiscalResidence }}</dt>
            <dd>{{ receiverFiscalResidence }}</dd>
          </div>
        </dl>
      </section>

    </div>
  </article>
</template>

<script>
import { parsePresentationDate } from '@/utils/datePresentation';
import { useDomainText } from '@/composables/text/useDomainText';

const { catalog: invoiceText } = useDomainText('invoices');
const { catalog: billingDataText } = useDomainText('billingData');

export default {
  name: 'InvoiceDetailFiscalSummaryCard',
  props: {
    issuer: {
      type: Object,
      default: () => ({}),
    },
    selectedReceiver: {
      type: Object,
      default: null,
    },
    invoiceInfo: {
      type: Object,
      default: () => ({}),
    },
    paymentMethodLabel: {
      type: String,
      default: '',
    },
    paymentFormLabel: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      text: invoiceText,
      billingDataText,
      showFullSummary: false,
    };
  },
  computed: {
    fallbackValue() {
      return String(this.text?.detail?.page?.dateFallback || '-').trim() || '-';
    },
    issuerBusinessName() {
      return String(this.issuer?.businessName || '').trim() || this.fallbackValue;
    },
    issuerRfc() {
      return String(this.issuer?.rfc || '').trim() || this.fallbackValue;
    },
    issuerTaxRegime() {
      return String(this.issuer?.taxRegime || '').trim() || this.fallbackValue;
    },
    issuerFiscalResidence() {
      return String(this.issuer?.fiscalResidence || '').trim() || this.fallbackValue;
    },
    receiverBusinessName() {
      return String(this.selectedReceiver?.meta?.businessName || this.selectedReceiver?.businessName || '').trim() || this.fallbackValue;
    },
    receiverRfc() {
      return String(this.selectedReceiver?.meta?.rfc || this.selectedReceiver?.rfc || '').trim() || this.fallbackValue;
    },
    receiverCfdiUse() {
      return String(this.selectedReceiver?.meta?.cfdiUse || this.selectedReceiver?.cfdiUse || '').trim() || this.fallbackValue;
    },
    receiverTaxRegime() {
      return String(this.selectedReceiver?.meta?.taxRegime || this.selectedReceiver?.taxRegime || '').trim() || this.fallbackValue;
    },
    receiverFiscalResidence() {
      return String(this.selectedReceiver?.meta?.fiscalResidence || this.selectedReceiver?.fiscalResidence || '').trim() || this.fallbackValue;
    },
    resolvedDateTime() {
      const raw = String(this.invoiceInfo?.rawDate || '').trim();
      const parsed = parsePresentationDate(raw);

      if (!parsed) {
        return raw !== '' ? raw : this.fallbackValue;
      }

      return new Intl.DateTimeFormat('es-MX', {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
      }).format(parsed);
    },
    resolvedSerialFolio() {
      const series = String(this.invoiceInfo?.series || '').trim();
      const folio = String(this.invoiceInfo?.folio || '').trim();
      const serialFolio = `${series}${folio}`.trim();
      return serialFolio !== '' ? serialFolio : this.fallbackValue;
    },
    resolvedUuid() {
      return String(this.invoiceInfo?.uuid || '').trim() || this.fallbackValue;
    },
    resolvedPaymentMethod() {
      const label = String(this.paymentMethodLabel || '').trim();
      if (label !== '') {
        return label;
      }

      return String(this.invoiceInfo?.paymentMethod || '').trim() || this.fallbackValue;
    },
    resolvedPaymentForm() {
      const label = String(this.paymentFormLabel || '').trim();
      if (label !== '') {
        return label;
      }

      return String(this.invoiceInfo?.paymentForm || '').trim() || this.fallbackValue;
    },
    resolvedIssueDateLabel() {
      return String(this.text?.detail?.cards?.fiscalSummary?.fields?.issueDate || '').trim() || this.fallbackValue;
    },
    fiscalToggleMoreLabel() {
      return String(this.text?.detail?.cards?.fiscalSummary?.actions?.showAll || '').trim() || this.fallbackValue;
    },
    fiscalToggleLessLabel() {
      return String(this.text?.detail?.cards?.fiscalSummary?.actions?.showLess || '').trim() || this.fallbackValue;
    },
    showForeignCurrencyRow() {
      return this.resolvedCurrencyCode !== 'MXN';
    },
    resolvedCurrencyCode() {
      return String(this.invoiceInfo?.currency || 'MXN').trim().toUpperCase() || 'MXN';
    },
    resolvedExchangeRate() {
      const raw = this.invoiceInfo?.exchangeRate;
      const numeric = Number(raw);
      if (Number.isFinite(numeric) && numeric > 0) {
        return String(numeric);
      }

      return String(raw || '').trim();
    },
    currencyExchangeLabel() {
      return String(this.text?.detail?.cards?.fiscalSummary?.fields?.currencyExchange || '').trim() || this.fallbackValue;
    },
    currencyExchangeValue() {
      const exchangeRate = this.resolvedExchangeRate || this.fallbackValue;
      return `${this.resolvedCurrencyCode} / ${exchangeRate}`;
    },
  },
};
</script>

<style scoped>
.invoice-detail-fiscal-card {
  height: 100%;
  padding: 14px 16px;
}

.invoice-detail-fiscal-toggle {
  border: 0;
  background: transparent;
  padding: 0;
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  color: #0070cd;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
}

.invoice-detail-fiscal-toggle:hover {
  color: #005a9f;
}

.invoice-detail-fiscal-list {
  margin: 0;
  padding: 0;
}

.invoice-detail-fiscal-row {
  display: grid;
  grid-template-columns: minmax(118px, auto) minmax(0, 1fr);
  align-items: baseline;
  column-gap: 10px;
  margin-bottom: 8px;
}

.invoice-detail-fiscal-row:last-child {
  margin-bottom: 0;
}

.invoice-detail-fiscal-row--with-separator {
  border-bottom: 1px solid rgba(148, 163, 184, 0.32);
  padding-bottom: 8px;
  margin-bottom: 10px;
}

.invoice-detail-fiscal-row dt {
  margin: 0;
  font-size: 11px;
  line-height: 1.35;
  font-weight: 600;
  color: #7b879a;
}

.invoice-detail-fiscal-row dd {
  margin: 0;
  font-size: 12px;
  line-height: 1.35;
  font-weight: 600;
  color: #334155;
  word-break: break-word;
}

.invoice-detail-fiscal-expanded {
  border-top: 1px solid rgba(148, 163, 184, 0.34);
  margin-top: 12px;
  padding-top: 12px;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.invoice-detail-fiscal-block {
  min-width: 0;
}

.invoice-detail-fiscal-block h4 {
  margin: 0 0 8px;
  font-size: 10px;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

@media (max-width: 900px) {
  .invoice-detail-fiscal-expanded {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 767px) {
  .invoice-detail-fiscal-row {
    grid-template-columns: minmax(98px, auto) minmax(0, 1fr);
    margin-bottom: 7px;
  }

  .invoice-detail-fiscal-row dt {
    font-size: 11px;
  }

  .invoice-detail-fiscal-row dd {
    font-size: 12px;
  }
}
</style>
